import type React from 'react'
import {useCallback, useEffect, useState} from 'react'

export function useFilter<T>({
  data,
  setFilterValue,
  onSelect,
}: {
  data: ReadonlyArray<T>
  setFilterValue: (nextValue: string) => void
  onSelect: (selected: T) => void
}) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined)
  const itemsCount = data.length
  const lastIndex = data.length - 1
  const onFilterSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (itemsCount === 1) {
        onSelect(data[0])
      }
    },
    [data, itemsCount, onSelect],
  )
  const onFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilterValue(event.currentTarget.value)
      setActiveIndex(undefined)
    },
    [setFilterValue],
  )
  const onFilterClear = useCallback(() => {
    setFilterValue('')
    setActiveIndex(undefined)
  }, [setFilterValue])
  const resetActiveIndex = useCallback(() => {
    setActiveIndex(undefined)
  }, [])
  const onMouseMove = useCallback(() => {
    setActiveIndex(undefined)
    document.removeEventListener('mousemove', onMouseMove)
  }, [])
  useEffect(() => () => document.removeEventListener('mousemove', onMouseMove), [onMouseMove])
  const setActiveIndexAndMouseHandler = useCallback(
    (index: number) => {
      setActiveIndex(index)
      document.addEventListener('mousemove', onMouseMove)
    },
    [onMouseMove],
  )
  const onFilterKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const modifier = navigator.platform === 'MacIntel' ? event.metaKey : event.ctrlKey
      switch (event.key) {
        case 'Escape':
          setFilterValue('')
          break
        case 'Home':
          setActiveIndexAndMouseHandler(0)
          break
        case 'End':
          setActiveIndexAndMouseHandler(lastIndex)
          break
        case 'ArrowUp':
          if (modifier) {
            setActiveIndexAndMouseHandler(0)
          } else if (activeIndex != null) {
            if (activeIndex > 0) {
              setActiveIndexAndMouseHandler(activeIndex - 1)
            } else {
              setActiveIndexAndMouseHandler(lastIndex)
            }
          }
          break
        case 'ArrowDown':
          if (modifier) {
            setActiveIndexAndMouseHandler(lastIndex)
          } else if (activeIndex != null) {
            if (activeIndex < lastIndex) {
              setActiveIndexAndMouseHandler(activeIndex + 1)
            } else {
              setActiveIndexAndMouseHandler(0)
            }
          } else {
            setActiveIndexAndMouseHandler(0)
          }
          break
        case 'Enter':
          if (activeIndex != null) {
            onSelect(data[activeIndex])
          }
          break
        default:
          break
      }
    },
    [activeIndex, data, lastIndex, onSelect, setActiveIndexAndMouseHandler, setFilterValue],
  )

  return {
    onFilterSubmit,
    onFilterChange,
    onFilterClear,
    resetActiveIndex,
    onFilterKeyPress,
    activeIndex,
  }
}
